import { FC } from 'react';
import { useTranslation } from '~/shared/utils/translation';
import { ResetButton } from '~/shared/components/Button/ResetButton';
import SwitchElement from '~/shared/components/form/components/controllable-elements/switch-element';
import { useStoreAvailability } from '~/features/productList/hooks/useStoreAvailability';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';
import { Facets } from '~/services/instantsearch';
import { boldPlaceholderTail } from './boldPlaceholderTail';
import styles from './StoreAvailabilityToggles.module.scss';

type StoreAvailabilityTogglesProps = {
    onSelectStore: () => void;
};

export const StoreAvailabilityToggles: FC<StoreAvailabilityTogglesProps> = ({ onSelectStore }) => {
    const { translate } = useTranslation();
    const { trackFilter } = useDrawerFilterTracking();

    const {
        isOnlineEnabled,
        isStoreEnabled,
        selectedStores,
        setIsOnlineEnabled,
        setIsStoreEnabled,
    } = useStoreAvailability();

    const selectedStoreCount = selectedStores.length;

    const handleOnlineChange = (enabled: boolean) => {
        trackFilter(Facets.STORE_WEB, 'in_stock_web', { isRefinement: false, isRefined: !enabled });
        setIsOnlineEnabled(enabled);
    };

    const handleStoreChange = (enabled: boolean) => {
        trackFilter(Facets.STORE, 'in_stock_store', { isRefinement: false, isRefined: !enabled });
        setIsStoreEnabled(enabled);
    };

    const storeLabel =
        selectedStoreCount === 0
            ? translate('plp.storeAvailability.selectStore')
            : selectedStoreCount === 1
              ? boldPlaceholderTail(
                    translate('plp.storeAvailability.singleStore'),
                    translate('plp.storeAvailability.singleStore', {
                        store: selectedStores[0],
                    }),
                )
              : boldPlaceholderTail(
                    translate('plp.storeAvailability.multipleStores'),
                    translate('plp.storeAvailability.multipleStores', {
                        count: `${selectedStoreCount}`,
                    }),
                );

    const editLinkLabel =
        selectedStoreCount === 0
            ? translate('plp.storeAvailability.selectStoreLink')
            : translate('plp.storeAvailability.editStoreLink');

    return (
        <div className={styles.container}>
            <SwitchElement
                checked={isOnlineEnabled}
                onChange={(event) => handleOnlineChange(event.target.checked)}
                label={translate('plp.storeAvailability.online')}
            />

            <div className={styles.storeToggle}>
                <SwitchElement
                    checked={isStoreEnabled}
                    onChange={(event) => handleStoreChange(event.target.checked)}
                    label={storeLabel}
                />

                {isStoreEnabled && (
                    <ResetButton className={styles.editLink} onClick={onSelectStore}>
                        {editLinkLabel}
                    </ResetButton>
                )}
            </div>
        </div>
    );
};

export default StoreAvailabilityToggles;
